<template>
  <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
    <div v-for="(child, index) in areasWithPartnerAndCaretaker" :key="child.key">
      <div class="flex items-center">
        <div class="flex flex-col flex-1">
          <ContractArea
            :index="index"
            :data="child.value"
            :customerId="customerId"
            :editMode="editMode"
            :areas="getAvailableAreas(index)"
            :isExistingArea="isAlreadyExistingAreaRelation(child.value.areaId)"
            @update:data="(data) => (child.value = { ...child.value, ...data })"
          />
        </div>
        <RemoveButton
          v-if="editMode && areasWithPartnerAndCaretaker.length > 1"
          @click="() => onRemove(index)"
        />
      </div>
      <Divider v-if="index !== areasWithPartnerAndCaretaker.length - 1" class="" />
    </div>
    <div class="flex mt-4">
      <Button
        v-if="editMode"
        :disabled="!customerId"
        outlined
        icon="pi pi-plus"
        :label="t('labels.add')"
        @click="onAddAreaWithPartnerAndCaretaker"
      />
      <div v-if="editMode && !isNew && isAnyAreaChange" class="ml-auto">
        <ButtonGroup class="bg-primary">
          <Button icon="pi pi-refresh" outlined @click="() => confirmResetChanges()" />
          <Button
            icon="pi pi-check"
            :label="t('buttons.save') + ' ' + t('labels.areas').toLocaleLowerCase()"
            @click="() => onSaveAreaWithPartnerAndCaretaker()"
          />
        </ButtonGroup>
      </div>
    </div>
    <Confirm ref="confirmDialog" />
  </SectionWrapper>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import { useFieldArray } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import RemoveButton from '@/components/buttons/RemoveButton.vue';
import Confirm from '@/components/confirm/Confirm.vue';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { ContractingPartyToContractV1Dto } from '@/openapi';

import { getNewAreaValue } from '../configs/contractFormConfig';
import type {
  AreaWithPartnerAndCaretaker,
  ContractAreaWithPartnerAndCaretakerSimple,
} from '../contractsTypes';
import {
  useContracts,
  useContractsAreasWithPartnerAndCaretakerConverter,
} from '../hooks/useContracts';

import ContractArea from './ContractArea.vue';

const props = defineProps<{
  data: ContractAreaWithPartnerAndCaretakerSimple | undefined;
  isNew: boolean;
  customerId: string | undefined;
  editMode: boolean;
  areas: SelectModel[];
  fetchData: () => Promise<void>;
  handleValidateAreas: (areas: AreaWithPartnerAndCaretaker[]) => boolean;
}>();

const { t } = useI18n();
const {
  convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDeleteManyDto,
  convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerCreateManyDto,
  convertAreaWithPartnerAndCaretakerToPartnerAndCaretakerPartyPatchManyDto,
} = useContractsAreasWithPartnerAndCaretakerConverter();
const { deleteAreaRelation, assignAreaRelation, updateContractingParties } = useContracts();

const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);

const fieldArray = useFieldArray<AreaWithPartnerAndCaretaker>('areasWithPartnerAndCaretaker');
const { fields: areasWithPartnerAndCaretaker } = fieldArray;

let hasSyncedCurrentData = false;

// Auto-add an area when customer is selected and no areas exist yet
watch(
  () => props.customerId,
  (newCustomerId) => {
    if (newCustomerId && areasWithPartnerAndCaretaker.value.length === 0) {
      fieldArray.push(getNewAreaValue());
    }
  },
);

// Reset sync flag when loading a different contract
watch(
  () => props.data?.id,
  () => {
    hasSyncedCurrentData = false;
  },
);

// Sync loaded data to fieldArray when contract data is loaded
watch(
  () => props.data,
  (contractData) => {
    if (!contractData || props.isNew) {
      hasSyncedCurrentData = false;
      return;
    }

    const loadedAreas = contractData.areasWithPartnerAndCaretaker;
    if (!loadedAreas?.length) return;

    const hasValidData = loadedAreas.some((area: AreaWithPartnerAndCaretaker) => area.areaId);
    if (!hasValidData) return;

    // Check if fieldArray already has this data to prevent re-syncing
    const currentFieldArrayKey = JSON.stringify(
      areasWithPartnerAndCaretaker.value.map((f) => f.value.areaId),
    );
    const loadedAreasKey = JSON.stringify(
      loadedAreas.map((a: AreaWithPartnerAndCaretaker) => a.areaId),
    );

    if (currentFieldArrayKey === loadedAreasKey) return;

    if (!hasSyncedCurrentData) {
      hasSyncedCurrentData = true;
      const copiedAreas = loadedAreas.map((area: AreaWithPartnerAndCaretaker) => ({ ...area }));
      void nextTick(() => {
        fieldArray.replace(copiedAreas);
      });
    }
  },
  { immediate: false, deep: true },
);

const isAnyAreaChange = computed(() => {
  const currentAreas = areasWithPartnerAndCaretaker.value
    .map((field) => field.value)
    .filter((area) => Boolean(area.areaId));
  const originalAreas = (props.data?.areasWithPartnerAndCaretaker ?? []).filter((area) =>
    Boolean(area.areaId),
  );

  return !isEqual(currentAreas, originalAreas);
});

const getAvailableAreas = (currentIndex: number): SelectModel[] => {
  const selectedAreaIds = areasWithPartnerAndCaretaker.value
    .map((field, index) => (index !== currentIndex ? field.value.areaId : null))
    .filter((areaId: string | null) => !!areaId);

  return props.areas.filter((area) => !selectedAreaIds.includes(String(area.id)));
};

const handleFetchData = async () => {
  await props.fetchData();
};

async function confirmResetChanges() {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isResetConfirmed();

  if (isConfirmed) {
    fieldArray.replace(props.data?.areasWithPartnerAndCaretaker ?? []);
  }
}

const onAddAreaWithPartnerAndCaretaker = (): void => {
  fieldArray.push({
    areaId: '',
    partnerPartyId: '',
    partnerRole: ContractingPartyToContractV1Dto.role.PROPERTY_LANDLORD,
    partnerId: '',
    partnerGroupId: '',
    caretakerRole: ContractingPartyToContractV1Dto.role.PROPERTY_MANAGER,
  });
};

const onSaveAreaWithPartnerAndCaretaker = async (): Promise<void> => {
  if (!props.data) return;

  const areasWithPartnerAndCaretakerValues = areasWithPartnerAndCaretaker.value.map(
    (field) => field.value,
  );

  if (!props.handleValidateAreas(areasWithPartnerAndCaretakerValues)) return;

  const areasToAssign = areasWithPartnerAndCaretakerValues.filter(
    (x) => !props.data!.areasWithPartnerAndCaretaker?.some((y) => y.areaId === x.areaId),
  );

  const areasToUpdate = areasWithPartnerAndCaretakerValues.filter((x) =>
    props.data!.areasWithPartnerAndCaretaker?.some((y) => y.areaId === x.areaId),
  );

  await assignAreaRelation(
    String(props.data.id),
    convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerCreateManyDto(areasToAssign),
  );

  await updateContractingParties(
    String(props.data.id),
    convertAreaWithPartnerAndCaretakerToPartnerAndCaretakerPartyPatchManyDto(areasToUpdate),
  );
  await handleFetchData();
};

const isAlreadyExistingAreaRelation = (areaId: string): boolean => {
  return props.data?.areasWithPartnerAndCaretaker?.some((x) => x.areaId === areaId) ?? false;
};

const onRemove = async (index: number): Promise<void> => {
  if (!confirmDialog.value) return;

  const areaWithPartnerAndCaretaker = areasWithPartnerAndCaretaker.value[index];
  if (!areaWithPartnerAndCaretaker) return;

  if (
    !props.isNew &&
    props.data &&
    isAlreadyExistingAreaRelation(areaWithPartnerAndCaretaker.value.areaId)
  ) {
    const isConfirmed = await confirmDialog.value.isConfirmed();

    if (!isConfirmed) {
      return;
    }
    await deleteAreaRelation(
      String(props.data.id),
      convertAreaWithPartnerAndCaretakerToAreaWithPartnerAndCaretakerDeleteManyDto(
        areaWithPartnerAndCaretaker.value,
      ),
    );
    await handleFetchData();
  } else {
    fieldArray.remove(index);
  }
};
</script>
